import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { getSupabaseClient } from "@veorest/api-client";
import { AdminAuthLayout } from "../components/auth/AdminAuthLayout";
import { AuthField, AuthSubmit } from "../components/auth/AuthField";
import { ROUTES } from "../routes";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const supabase = getSupabaseClient();
    supabase.auth.getSession().then(({ data }) => {
      setReady(Boolean(data.session));
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setIsSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      navigate(ROUTES.LOGIN);
    } catch {
      setError("Could not update password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminAuthLayout
      title="Set new password"
      subtitle="Choose a strong password for your Super Admin account."
      footerPrompt="Back to"
      footerLinkLabel="Sign in"
      footerLinkTo={ROUTES.LOGIN}
    >
      {!ready ? (
        <div className="pill pill-warning mb-4 w-full justify-center">
          Open this page from your password reset email link.
        </div>
      ) : null}
      {error ? <div className="pill pill-danger mb-4 w-full justify-center">{error}</div> : null}

      <form onSubmit={handleSubmit} className="auth-form space-y-4">
        <AuthField
          id="new-password"
          label="New password"
          type="password"
          icon={Lock}
          value={password}
          onChange={setPassword}
          required
          minLength={8}
          disabled={!ready}
        />
        <AuthField
          id="confirm-password"
          label="Confirm password"
          type="password"
          icon={Lock}
          value={confirm}
          onChange={setConfirm}
          required
          minLength={8}
          disabled={!ready}
        />
        <AuthSubmit loading={isSubmitting || !ready}>
          {isSubmitting ? "Saving…" : "Update password"}
        </AuthSubmit>
      </form>
    </AdminAuthLayout>
  );
}
